<template>

    <!-- Add Items !-->
    <card styles="padding: 0;" :show-separator="false" :show-title-row="false">
        <ui-tabs ref="tabs" type="icon-and-text" background-color="clear" class="content-creation-card-tabs">

            <!-- Notes !-->
            <ui-tab id="note" class="content-creation-card-notes-tab">
                <div slot="header">
                   <ui-icon slot="icon" icon="note"></ui-icon>
                   <span>Note</span>
                </div>
                <div class="container-fluid">
                    <div class="row">
                        <div class="col">
                            <note-pane 
                                ref="note"
                                :is-busy="isAnyBusy"
                                :owners="owners"
                                @saving="isNoteBusy = true"
                                @saved="isNoteBusy = false; $emit('created')"
                                @error="isNoteBusy = false"
                            />
                            <div class="content-creation-card-note-buttons text-right">
                                <ui-button 
                                    color="primary" 
                                    size="small" 
                                    @click="$refs.note.save()"
                                    :loading="isNoteBusy"
                                    :disabled="isAnyBusy"
                                >Save</ui-button>
                                <ui-button 
                                    size="small" 
                                    @click="$refs.note.reset()"
                                    :disabled="isAnyBusy"
                                >Clear</ui-button>
                            </div>
                        </div>
                    </div>
                </div>
            </ui-tab>

            <!-- Activity !-->
            <ui-tab id="activity" class="content-creation-card-activity-tab">
                <div slot="header">
                   <ui-icon slot="icon" icon="today"></ui-icon>
                   <span>Diary</span>
                </div>
                <div class="container-fluid">
                    <div class="row">
                        <div class="col">
                            <activity-pane
                                ref="activity"
                                :is-busy="isAnyBusy"
                                :owners="owners"
                                @saving="isActivityBusy = true"
                                @saved="isActivityBusy = false; $emit('created'); switchTab('note')"
                                @error="isActivityBusy = false"
                            />
                            <div class="content-creation-card-activity-buttons">
                                <ui-button 
                                    color="primary" 
                                    size="small"
                                    @click="$refs.activity.save()"
                                    :loading="isActivityBusy"
                                    :disabled="isAnyBusy"
                                >Save</ui-button>
                                <ui-button 
                                    size="small"
                                    @click="$refs.activity.reset()"
                                    :disabled="isAnyBusy"
                                >Clear</ui-button>
                            </div>
                        </div>
                    </div>
                </div>
            </ui-tab>

            <!-- Email !-->
            <ui-tab v-if="can('access.inbox')" id="email" class="content-creation-card-email-tab">
                <div slot="header">
                   <ui-icon icon-set="material-icons-outlined" slot="icon" icon="email"></ui-icon>
                   <span>Email</span>
                </div>
                <div class="container-fluid">
                    <div class="row">
                        <div class="col">
                            <compose-email-pane
                                v-if="contact.email"
                                ref="emailPane"
                                :message-defaults="messageDefaults"
                                hide-to-field
                                @sent="$emit('created'); $refs.emailPane.reset(); switchTab('note')"
                            ></compose-email-pane>
                            <div v-else>
                                <hero-error-message
                                    title="Email Address Missing"
                                    detail="Add a valid email address to the contact and try again."
                                ></hero-error-message>
                            </div>
                        </div>
                    </div>
                </div>
            </ui-tab>

            <!-- SMS !-->
            <ui-tab v-if="can('access.inbox')" id="text" class="content-creation-card-txt-tab">
                <div slot="header">
                   <ui-icon slot="icon" icon="phone_iphone"></ui-icon>
                   <span>TXT</span>
                </div>
                <div class="container-fluid">
                    <div class="row">
                        <div class="col">
                            <compose-text-pane
                                ref="txtPane"
                                :message-defaults="messageDefaults"
                                v-if="contact.primary_telephone"
                                hide-to-field
                                @sent="$emit('created'); $refs.txtPane.reset();  switchTab('note')"
                            ></compose-text-pane>
                            <div v-else>
                                <hero-error-message
                                    title="Telephone Number Missing"
                                    detail="Add a valid primary telephone number to the contact and try again."
                                ></hero-error-message>
                            </div>
                        </div>
                    </div>
                </div>
            </ui-tab>

        </ui-tabs>
    </card>
</template>

<style lang="scss">
    .content-creation-card-tabs {
        margin-bottom: 0 !important;

        .message-quill-editor {
            .ql-container .ql-editor {
                min-height: 200px;
            }
        }

        .ui-tabs__body { padding: 0; }
        .ui-tab-header-item {
            height: auto; 
            padding: 1rem 1rem; 
            font-weight: bold; 
            text-transform: none;
        }

        .content-creation-card-activity-tab {
            padding: 1rem 1rem 2rem 1rem;

            .content-creation-card-activity-buttons {
                padding: 1rem 0 .5rem;
            }
        }

        .content-creation-card-txt-tab, .content-creation-card-email-tab {
            padding: 2rem 1rem;
        }

        .content-creation-card-notes-tab {
            background: #fffcdc;
            padding-bottom: 1rem;

            .ql-toolbar {
              position: absolute;
              bottom: -1rem;
              width: 100%;
              transform: translateY(100%);
            }

            .ql-toolbar, .ql-container {
                border: none;
            }

            .content-creation-card-note-buttons {
                padding: 1rem 1rem 0;
            }
        }
    }
</style>

<script>
    import NotePane from '../../forms/NotePane'
    import ActivityPane from '../../forms/ActivityPane'
    import Card from '../../elements/Card'
    import ComposeEmailPane from "../../inbox/ComposeEmailPane";
    import ComposeTextPane from "../../inbox/ComposeTextPane";
    import {mapGetters} from "vuex";

    export default {
        components: {
            ComposeTextPane,
            ComposeEmailPane,
            NotePane,
            ActivityPane,
            Card
        },
        props: {
            owners: {
                type: Array,
                required: true,
            }
        },
        watch: {
            owners: {
                handler() {
                    this.setMessageDefaults()
                },
                deep: true
            }
        },
        data() {
            return {
                isNoteBusy: false,
                isActivityBusy: false,
                messageDefaults: {
                    to: null,
                    message_box: null
                },
            }
        },
        computed: {
            ...mapGetters({
                can: 'user/can'
            }),
            isAnyBusy() {
                return this.isNoteBusy || this.isActivityBusy
            },
            contact() {
                if (this.owners[0].type === 'contact') {
                    return this.owners[0].object
                } else if (this.owners[0].type === 'lead') {
                    return this.owners[0].object.contact
                }
            }
        },
        methods: {
            switchTab(handle) {
                this.$refs.tabs.setActiveTab(handle)
            },
            setMessageDefaults() {
                this.messageDefaults.to = this.contact

                this.messageDefaults.message_box = this.$store.getters['inbox/messageBoxes/first']

                if (this.$refs.emailPane) {
                    this.$refs.emailPane.reset()
                }

                if (this.$refs.txtPane) {
                    this.$refs.txtPane.reset()
                }
            }
        },
        mounted() {
            if (this.$store.getters['inbox/messageBoxes/all'].length === 0) {
                this.$store.dispatch('inbox/messageBoxes/load').then(() => {
                    this.setMessageDefaults()
                })
            } else {
                this.setMessageDefaults()
            }
        }
    }
</script>